import { useState } from 'react';
import { Button, Modal, cn } from '@jywer/ui';

export function PostponeModal({
  onCancel,
  onSubmit,
}: {
  onCancel: () => void;
  onSubmit: (body: {
    mode: 'new_trigger_task' | 'future_date';
    reason: string;
    trigger_task_id?: string;
    until_date?: string;
  }) => Promise<void> | void;
}) {
  const [mode, setMode] = useState<'new_trigger_task' | 'future_date'>('future_date');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const [taskId, setTaskId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = reason.trim() && (mode === 'future_date' ? date : taskId);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        mode,
        reason: reason.trim(),
        ...(mode === 'future_date' ? { until_date: date } : { trigger_task_id: taskId }),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ukendt fejl');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title="Udskyd faktura"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Annullér
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!canSubmit || submitting}
            loading={submitting}
          >
            {submitting ? 'Udskyder…' : 'Udskyd'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-ink-2">Tilstand</div>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              onClick={() => setMode('future_date')}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs',
                mode === 'future_date'
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line-2 bg-paper-0 text-ink-1',
              )}
            >
              Bestemt dato
            </button>
            <button
              type="button"
              onClick={() => setMode('new_trigger_task')}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs',
                mode === 'new_trigger_task'
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line-2 bg-paper-0 text-ink-1',
              )}
            >
              Ny ClickUp-opgave
            </button>
          </div>
        </div>

        {mode === 'future_date' ? (
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-2">
              Udskyd til
            </span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded border border-line-2 px-2 py-1.5"
            />
          </label>
        ) : (
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-2">
              ClickUp task ID
            </span>
            <input
              type="text"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              placeholder="task123"
              className="w-full rounded border border-line-2 px-2 py-1.5"
            />
          </label>
        )}

        <label className="block text-sm">
          <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-2">
            Begrundelse
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="w-full rounded border border-line-2 px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-sm text-status-err">{error}</div> : null}
      </div>
    </Modal>
  );
}
